import React, { useState } from 'react';
import { DownloadIcon, KeyRoundIcon, PlugIcon, TrashIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Field';
import { Toggle } from '../../components/ui/ChoiceGroup';
import { useAccount } from '../../contexts/AccountContext';
import { SERVICE_SEAMS } from '../../utils/havazon';
import { SIGNALS } from '../../types/intelligence';
import type { VisibilityLevel, VisibilitySurface } from '../../types/identity';
const SURFACES: {
  key: VisibilitySurface;
  label: string;
  description: string;
}[] = [{
  key: 'academic',
  label: 'Academic profile',
  description: 'Programme, level, courses and results.'
}, {
  key: 'housing',
  label: 'Housing activity',
  description: 'Applications, saved listings and current tenancy.'
}, {
  key: 'roommate',
  label: 'Roommate profile',
  description: 'Living preferences shown to potential roommates.'
}, {
  key: 'career',
  label: 'Career profile',
  description: 'CV, skills and portfolio shown to verified employers.'
}, {
  key: 'contact',
  label: 'Contact details',
  description: 'Phone number and email address.'
}];
const LEVELS: {
  value: VisibilityLevel;
  label: string;
}[] = [{
  value: 'private',
  label: 'Only me'
}, {
  value: 'connections',
  label: 'People I accept'
}, {
  value: 'campus',
  label: 'People at my institution'
}, {
  value: 'public',
  label: 'Anyone on Campuzon'
}];
const CHANNELS = [{
  key: 'push',
  label: 'Push notifications',
  on: true
}, {
  key: 'email',
  label: 'Email',
  on: true
}, {
  key: 'sms',
  label: 'SMS',
  on: false
}];
export function Profile() {
  const {
    account,
    institutionName,
    campusName,
    setVisibility,
    signals,
    toggleSignal
  } = useAccount();
  const [channels, setChannels] = useState(CHANNELS);
  const activeSignals = SIGNALS.filter(signal => signals[signal.key]).length;
  return <div className="space-y-6" data-id="element-10972" data-mp="3qqbk6ebku8iz">
      <PageHeader eyebrow="Your account" title={account.fullName} description={`${account.email} · one Campuzon account across academics, housing, campus, career and support.`} actions={<Badge tone="brand" data-id="element-10974" data-mp="3c7nzzlx1aaj">{account.academic.status}</Badge>} data-id="element-10973" data-mp="3qpjpr6tpugua" />

      <div className="grid gap-6 lg:grid-cols-3" data-id="element-10975" data-mp="32te8unlus4wb">
        <div className="space-y-6 lg:col-span-2" data-id="element-10976" data-mp="32upd0oj7neny">
          <Panel title="Identity" bodyClassName="p-0" data-id="element-10977" data-mp="37qxnhomgqjos">
            <dl className="divide-y divide-line" data-id="element-10978" data-mp="37tk5ki6zchl9">
              {[{
              label: 'Full name',
              value: account.fullName
            }, {
              label: 'Matric number',
              value: account.academic.matricNumber
            }, {
              label: 'Institution',
              value: institutionName
            }, {
              label: 'Campus',
              value: campusName
            }, {
              label: 'Faculty',
              value: account.academic.faculty
            }, {
              label: 'Department',
              value: account.academic.department
            }, {
              label: 'Programme',
              value: account.academic.programme
            }, {
              label: 'Level',
              value: account.academic.level
            }, {
              label: 'Phone',
              value: account.phone
            }].map(row => <div key={row.label} className="flex flex-wrap justify-between gap-2 px-5 py-3" data-id="element-10979" data-mp="seyicpizo845">
                  <dt className="text-[13px] text-ink-muted" data-id="element-10980" data-mp="nh6se11wmw2l">{row.label}</dt>
                  <dd className="cz-num text-[13px] font-medium text-ink" data-id="element-10981" data-mp="ifhe8cjdy2aa">{row.value}</dd>
                </div>)}
            </dl>
          </Panel>

          <Panel title="Privacy" description="Sensitive information is private by default. Change what each surface exposes." bodyClassName="p-0" data-id="element-10982" data-mp="9fuc1oiu0sg6">
            <ul className="divide-y divide-line" data-id="element-10983" data-mp="27t4b9x3xgbks">
              {SURFACES.map(surface => <li key={surface.key} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center" data-id="element-10984" data-mp="23s103nwtr3pw">
                  <div className="min-w-0 flex-1" data-id="element-10985" data-mp="2futs8c4lsi2b">
                    <p className="text-[13px] font-medium text-ink" data-id="element-10986" data-mp="21wng6m2qdmw7">{surface.label}</p>
                    <p className="mt-0.5 text-[12px] leading-5 text-ink-muted" data-id="element-10987" data-mp="25xyxm4bgb1tb">
                      {surface.description}
                    </p>
                  </div>
                  <div className="w-full sm:w-52" data-id="element-10988" data-mp="1s59a1vg9xou3">
                    <Select aria-label={`${surface.label} visibility`} value={account.visibility[surface.key]} onChange={event => setVisibility(surface.key, event.target.value as VisibilityLevel)} data-id="element-10989" data-mp="1wxsp6wum0s10">
                      {LEVELS.map(level => <option key={level.value} value={level.value} data-id="element-10990" data-mp="1e6jdqi1a3t6s">
                          {level.label}
                        </option>)}
                    </Select>
                  </div>
                </li>)}
            </ul>
          </Panel>

          <Panel title="Personalization" description="Recommendations only use the signals you leave switched on. Each one can be withdrawn on its own." action={<Badge tone={activeSignals === 0 ? 'neutral' : 'brand'} data-id="element-10992" data-mp="1djvhg2arkzj1">
                {activeSignals} of {SIGNALS.length} in use
              </Badge>} bodyClassName="p-0" data-id="element-10991" data-mp="13lpfxlsvdvep">
            <ul className="divide-y divide-line px-5" data-id="element-10993" data-mp="1n9f0p92e2vef">
              {SIGNALS.map(signal => <li key={signal.key} data-id="element-10994" data-mp="1iatyj43ku1nc">
                  <Toggle checked={signals[signal.key]} onChange={() => toggleSignal(signal.key)} label={signal.label} description={signal.description} data-id="element-10995" data-mp="1ibwo98p5xy1o" />
                </li>)}
            </ul>
            <p className="border-t border-line px-5 py-3 text-[12px] leading-5 text-ink-muted" data-id="element-10996" data-mp="oz5aley4ig4z">
              {activeSignals === 0 ? 'All signals are off, so every suggestion is now ranked by popularity and labelled as such.' : 'Withdrawing a signal never removes recommendations — affected suggestions fall back to popularity and say so.'}
            </p>
          </Panel>

          <Panel title="Havazon services" description="Campuzon is a Havazon venture. Shared infrastructure is reached through defined service boundaries." bodyClassName="p-0" data-id="element-10997" data-mp="17ntsrt5827d2">
            <ul className="divide-y divide-line" data-id="element-10998" data-mp="1hkorod5eq9rv">
              {SERVICE_SEAMS.map(seam => <li key={seam.service} className="flex gap-3 px-5 py-3.5" data-id="element-10999" data-mp="1lm0haioa6v02">
                  <PlugIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-11000" data-mp="3jfh0r9zig6ak" />
                  <div className="min-w-0 flex-1" data-id="element-11001" data-mp="3ngj4a30gn4ic">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-11002" data-mp="3fvynlzzavfxf">
                      <p className="text-[13px] font-semibold text-ink" data-id="element-11003" data-mp="3fx9sk34hetq4">{seam.service}</p>
                      <Badge tone={seam.status === 'connected' ? 'ok' : 'neutral'} data-id="element-11004" data-mp="3b3p5h64ffk5p">
                        {seam.status === 'connected' ? 'Connected' : 'Planned'}
                      </Badge>
                    </div>
                    <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-11005" data-mp="322h7kp5tolc5">{seam.capability}</p>
                    <p className="mt-0.5 text-[12px] leading-5 text-ink-subtle" data-id="element-11006" data-mp="2onfhnou5g82">{seam.note}</p>
                  </div>
                </li>)}
            </ul>
          </Panel>
        </div>

        <div className="space-y-6" data-id="element-11007" data-mp="3pw935r8t4zfr">
          <Panel title="Notifications" data-id="element-11008" data-mp="3kvbjfa85fxc5">
            {channels.map(channel => <Toggle key={channel.key} checked={channel.on} onChange={value => setChannels(current => current.map(item => item.key === channel.key ? {
            ...item,
            on: value
          } : item))} label={channel.label} description={channel.key === 'sms' ? 'Used for payment and safety messages only.' : undefined} data-id="element-11009" data-mp="2eezjp4poi7eg" />)}
            <p className="mt-3 border-t border-line pt-3 text-[12px] leading-5 text-ink-muted" data-id="element-11010" data-mp="w3hsu13jnlt0">
              Emergency and safety alerts are always delivered, regardless of these settings.
            </p>
          </Panel>

          <Panel title="Security" data-id="element-11011" data-mp="mceqyx2sj2so">
            <ul className="space-y-3 text-[13px]" data-id="element-11012" data-mp="md6d7hahzo6a">
              <li className="flex items-center justify-between gap-3" data-id="element-11013" data-mp="rbrcnehxe3to">
                <span className="text-ink" data-id="element-11014" data-mp="1f23t5dr8l8vv">Two-factor authentication</span>
                <Badge tone="ok" data-id="element-11015" data-mp="1f0smn66epn12">On</Badge>
              </li>
              <li className="flex items-center justify-between gap-3" data-id="element-11016" data-mp="1jtvm1uxv7qp6">
                <span className="text-ink" data-id="element-11017" data-mp="34izevcfghup6">Active sessions</span>
                <span className="cz-num text-ink-muted" data-id="element-11018" data-mp="34h4qtp8w1mgv">2 devices</span>
              </li>
              <li className="flex items-center justify-between gap-3" data-id="element-11019" data-mp="2zjw6bi09p6cg">
                <span className="text-ink" data-id="element-11020" data-mp="3dahthiwr82g5">Last sign-in</span>
                <span className="cz-num text-ink-muted" data-id="element-11021" data-mp="399xtm7ksqkqb">Today, 07:12</span>
              </li>
            </ul>
            <Button variant="secondary" className="mt-4 w-full" icon={<KeyRoundIcon className="h-4 w-4" data-id="element-11023" data-mp="2gljwrv5g1ugc" />} data-id="element-11022" data-mp="2lk3hxtc7r27n">
              Change password
            </Button>
          </Panel>

          <Panel title="Your data" data-id="element-11024" data-mp="2uluot1ihpf33">
            <div className="space-y-2" data-id="element-11025" data-mp="2uiystiq7d0ha">
              <Button variant="secondary" className="w-full" icon={<DownloadIcon className="h-4 w-4" data-id="element-11027" data-mp="9u19yhn0defe" />} data-id="element-11026" data-mp="2plp465an0sui">
                Download my data
              </Button>
              <Button variant="danger" className="w-full" icon={<TrashIcon className="h-4 w-4" data-id="element-11029" data-mp="2gd4nvxoq4wqn" />} data-id="element-11028" data-mp="4uy2yrngwy4s">
                Delete my account
              </Button>
            </div>
            <p className="mt-3 text-[12px] leading-5 text-ink-muted" data-id="element-11030" data-mp="2bhpbla6d78wc">
              Deletion removes your profile, roommate data and career profile. Tenancy and payment
              records are retained where the law requires it.
            </p>
          </Panel>
        </div>
      </div>
    </div>;
}